import {
  Alert,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import {
  IconAlertCircle,
  IconFilter,
  IconPlus,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { PanelActiveFilters } from "../../../components/admin/panels/PanelActiveFilters";
import { PanelAdminRow } from "../../../components/admin/panels/PanelAdminRow";
import {
  PanelFilterModal,
  PanelFilters,
} from "../../../components/admin/panels/PanelFilterModal";
import { PanelFormModal } from "../../../components/admin/panels/PanelFormModal";
import { PanelStatusConfirmModal } from "../../../components/admin/panels/PanelStatusConfirmModal";
import type { PanelSelectOption } from "../../../components/admin/panels/panelForm";
import { useCptCodesAdmin } from "../../../hooks/admin/useCptCodes";
import {
  useCreatePanel,
  useCreatePanelVersion,
  usePanels,
  useUpdatePanelStatus,
} from "../../../hooks/admin/usePanels";
import { useStainsAdmin } from "../../../hooks/admin/useStains";
import type {
  PanelDefinition,
  PanelListParams,
  PanelSortField,
  SavePanelInput,
  SortOrder,
} from "../../../services/admin/panels.service";
import { isBillableCptCode } from "../../../utils/billingCptCode";

const EMPTY_FILTERS: PanelFilters = { status: "", workflowKind: "" };

function buildOption(id: string, code: string, description: string) {
  return { value: id, label: `${code} — ${description}` };
}

export default function PanelsAdminPage() {
  const [editorOpened, { open: openEditor, close: closeEditor }] =
    useDisclosure(false);
  const [filterOpened, { open: openFilter, close: closeFilter }] =
    useDisclosure(false);
  const [editingPanel, setEditingPanel] = useState<PanelDefinition>();
  const [panelToToggle, setPanelToToggle] = useState<PanelDefinition>();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<PanelFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<PanelSortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const params = useMemo<PanelListParams>(
    () => ({
      page,
      limit: pageSize,
      search: debouncedSearch.trim() || undefined,
      status: filters.status || undefined,
      workflowKind: filters.workflowKind || undefined,
      sortBy,
      sortOrder,
    }),
    [debouncedSearch, filters, page, pageSize, sortBy, sortOrder]
  );
  const panelsQuery = usePanels(params);
  const stainsQuery = useStainsAdmin({ status: "active" });
  const cptCodesQuery = useCptCodesAdmin();
  const createPanel = useCreatePanel();
  const createVersion = useCreatePanelVersion();
  const updateStatus = useUpdatePanelStatus();
  const panels = useMemo(
    () => panelsQuery.data?.data ?? [],
    [panelsQuery.data]
  );
  const total = useMemo(() => panelsQuery.data?.total ?? 0, [panelsQuery.data]);
  const totalPages = useMemo(
    () => Math.max(1, panelsQuery.data?.totalPages ?? 1),
    [panelsQuery.data]
  );
  const visibleRange = useMemo(() => {
    if (total === 0) return "Showing 0 of 0";
    const first = (page - 1) * pageSize + 1;
    const last = Math.min(page * pageSize, total);
    return `Showing ${first}–${last} of ${total}`;
  }, [page, pageSize, total]);
  const hasActiveFilters = useMemo(
    () => Boolean(filters.status || filters.workflowKind),
    [filters]
  );
  const stainOptions = useMemo<PanelSelectOption[]>(
    () =>
      (stainsQuery.data ?? [])
        .filter((stain) => stain.isActive)
        .map((stain) => buildOption(stain.id, stain.code, stain.description))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [stainsQuery.data]
  );
  const cptOptions = useMemo<PanelSelectOption[]>(
    () =>
      (cptCodesQuery.data ?? [])
        .filter((cpt) => cpt.isActive && isBillableCptCode(cpt.code))
        .map((cpt) => buildOption(cpt.id, cpt.code, cpt.description))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [cptCodesQuery.data]
  );
  const isSaving = useMemo(
    () => createPanel.isPending || createVersion.isPending,
    [createPanel.isPending, createVersion.isPending]
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.currentTarget.value);
      setPage(1);
    },
    []
  );
  const handleSortChange = useCallback((value: string | null) => {
    const [field, direction] = (value ?? "name:asc").split(":");
    setSortBy(field as PanelSortField);
    setSortOrder(direction as SortOrder);
    setPage(1);
  }, []);
  const handlePageSizeChange = useCallback((value: string | null) => {
    setPageSize(Number(value ?? 20));
    setPage(1);
  }, []);
  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);
  const handleAdd = useCallback(() => {
    setEditingPanel(undefined);
    openEditor();
  }, [openEditor]);
  const handleEdit = useCallback(
    (panel: PanelDefinition) => {
      setEditingPanel(panel);
      openEditor();
    },
    [openEditor]
  );
  const handleSubmit = useCallback(
    async (input: SavePanelInput) => {
      if (editingPanel) {
        await createVersion.mutateAsync({ id: editingPanel.id, input });
      } else {
        await createPanel.mutateAsync(input);
      }
      closeEditor();
    },
    [closeEditor, createPanel, createVersion, editingPanel]
  );
  const handleToggleRequest = useCallback((panel: PanelDefinition) => {
    if (!panel.isSystemManaged) setPanelToToggle(panel);
  }, []);
  const handleToggleClose = useCallback(() => setPanelToToggle(undefined), []);
  const handleToggleConfirm = useCallback(async () => {
    if (!panelToToggle || panelToToggle.isSystemManaged) return;
    const updated = await updateStatus
      .mutateAsync({
        id: panelToToggle.id,
        isActive: !panelToToggle.isActive,
      })
      .then(() => true)
      .catch(() => false);
    if (!updated) return;
    setPanelToToggle(undefined);
  }, [panelToToggle, updateStatus]);
  const handleApplyFilters = useCallback(
    (nextFilters: PanelFilters) => {
      setFilters(nextFilters);
      setPage(1);
      closeFilter();
    },
    [closeFilter]
  );
  const handleClearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, []);
  const handleRefresh = useCallback(() => {
    panelsQuery.refetch();
  }, [panelsQuery]);

  return (
    <Container fluid py="xl">
      <Stack gap="lg" maw={1600} mx="auto">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Panels</Title>
            <Text size="sm" c="dimmed" mt="xs">
              Configure versioned stain groups and their CPT billing behavior.
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
            Create panel
          </Button>
        </Group>
        <Paper withBorder p="md">
          <Group align="flex-end" wrap="wrap">
            <TextInput
              label="Search panels"
              placeholder="Search by panel name or description"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={handleSearchChange}
              style={{ flex: 1, minWidth: 260 }}
            />
            <Select
              label="Sort"
              value={`${sortBy}:${sortOrder}`}
              onChange={handleSortChange}
              data={[
                { value: "name:asc", label: "Name A–Z" },
                { value: "name:desc", label: "Name Z–A" },
                { value: "updatedAt:desc", label: "Recently updated" },
                {
                  value: "currentVersionNumber:desc",
                  label: "Highest version",
                },
              ]}
              allowDeselect={false}
              w={220}
            />
            <Button
              variant="light"
              leftSection={<IconFilter size={16} />}
              onClick={openFilter}
            >
              {hasActiveFilters ? "Edit filters" : "Add filters"}
            </Button>
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              loading={panelsQuery.isFetching}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </Group>
          {hasActiveFilters && (
            <PanelActiveFilters
              filters={filters}
              onClear={handleClearFilters}
            />
          )}
        </Paper>
        <Paper withBorder p="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600}>Panels ({total})</Text>
              <Group gap="md">
                <Text size="sm" c="dimmed">
                  {visibleRange}
                </Text>
                <Select
                  label="Rows per page"
                  value={String(pageSize)}
                  onChange={handlePageSizeChange}
                  data={["20", "50", "100"]}
                  allowDeselect={false}
                  w={130}
                  size="xs"
                />
                {totalPages > 1 && (
                  <Pagination
                    value={page}
                    total={totalPages}
                    onChange={handlePageChange}
                    size="sm"
                  />
                )}
              </Group>
            </Group>
            {panelsQuery.isError ? (
              <Alert
                icon={<IconAlertCircle size={18} />}
                title="Panels could not be loaded"
                color="red"
                variant="light"
                role="alert"
              >
                <Group justify="space-between">
                  <Text size="sm">Refresh the list and try again.</Text>
                  <Button variant="light" color="red" onClick={handleRefresh}>
                    Retry
                  </Button>
                </Group>
              </Alert>
            ) : panelsQuery.isLoading ? (
              <Center py="xl">
                <Loader aria-label="Loading panels" />
              </Center>
            ) : panels.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                {hasActiveFilters || debouncedSearch
                  ? "No panels match your search or filters."
                  : "No panels have been configured yet."}
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={1050} h={600}>
                <Table striped highlightOnHover stickyHeader>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Panel</Table.Th>
                      <Table.Th>Components</Table.Th>
                      <Table.Th>Billing</Table.Th>
                      <Table.Th>Workflow</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {panels.map((panel) => (
                      <PanelAdminRow
                        key={panel.id}
                        panel={panel}
                        onEdit={handleEdit}
                        onToggleStatus={handleToggleRequest}
                      />
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Stack>
        </Paper>
      </Stack>
      <PanelFormModal
        key={`${editorOpened ? "open" : "closed"}-${editingPanel?.id ?? "new"}`}
        opened={editorOpened}
        panel={editingPanel}
        stainOptions={stainOptions}
        cptOptions={cptOptions}
        isSubmitting={isSaving}
        onClose={closeEditor}
        onSubmit={handleSubmit}
      />
      <PanelFilterModal
        key={`${filterOpened ? "open" : "closed"}-${filters.status}-${
          filters.workflowKind
        }`}
        opened={filterOpened}
        filters={filters}
        onClose={closeFilter}
        onApply={handleApplyFilters}
      />
      <PanelStatusConfirmModal
        panel={panelToToggle}
        isSubmitting={updateStatus.isPending}
        onClose={handleToggleClose}
        onConfirm={handleToggleConfirm}
      />
    </Container>
  );
}
